/** Common bottle / package sizes for liquid (ml) and powder (g) supplements */
export const DEFAULT_PACKAGE_SIZES = [10, 30, 60, 100, 125, 250, 500, 1000];

/** @param {string} [unit] 'ml' | 'g' */
export function formatAmount(n, unit = "ml") {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  const v = Number(n);
  const u = unit === "g" ? "g" : "ml";
  const compact = v >= 100 ? 0 : 2;
  return `${v.toFixed(compact)} ${u}`;
}

/** Remaining volume over bottle size, e.g. `250 / 500 ml`. */
export function formatRemainingOverBottle(remaining, bottleSize, unit = "ml") {
  const u = unit === "g" ? "g" : "ml";
  const formatVal = (n) => {
    if (n == null || !Number.isFinite(Number(n))) return null;
    const v = Number(n);
    const compact = v >= 100 ? 0 : 2;
    return v.toFixed(compact);
  };
  const left = formatVal(remaining);
  const total = formatVal(bottleSize);
  if (left == null && total == null) return "—";
  if (total == null) return `${left} ${u}`;
  if (left == null) return `— / ${total} ${u}`;
  return `${left} / ${total} ${u}`;
}

export function formatCost(n) {
  if (n == null || n === "") return "—";
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const TIME_FORMAT = { hour: "numeric", minute: "2-digit", hour12: true };

/** `datetime-local` value for the browser's current local time. */
export function toDatetimeLocalValue(d = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Convert a `datetime-local` value (or similar) to naive `YYYY-MM-DD HH:mm:ss`
 * for API storage — no UTC conversion (matches what the user picked).
 */
export function datetimeLocalToRecordedAt(value) {
  if (value == null || value === "") return undefined;
  const raw = String(value).trim();
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!match) {
    return raw.replace("T", " ").slice(0, 19);
  }
  const [, date, hh, mm, ss = "00"] = match;
  return `${date} ${hh}:${mm}:${ss}`;
}

/** Milliseconds for sorting naive app `recorded_at` values. */
export function recordedAtToMs(value) {
  const d = parseAppDateTime(value);
  return d ? d.getTime() : 0;
}

export function parseAppDateTime(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const raw = String(value).trim();
  const spaceMatch = raw.match(
    /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})(?::(\d{2}))?$/,
  );
  if (spaceMatch) {
    const [, y, m, d, hh, mm, ss = "0"] = spaceMatch;
    const date = new Date(
      Number(y),
      Number(m) - 1,
      Number(d),
      Number(hh),
      Number(mm),
      Number(ss),
    );
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const localIsoMatch = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/,
  );
  if (localIsoMatch) {
    const [, y, m, d, hh, mm, ss = "0"] = localIsoMatch;
    const date = new Date(
      Number(y),
      Number(m) - 1,
      Number(d),
      Number(hh),
      Number(mm),
      Number(ss),
    );
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatDateTime(iso) {
  if (!iso) return "—";
  const d = parseAppDateTime(iso);
  if (!d) return String(iso);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...TIME_FORMAT,
  });
}

export function formatDateOnly(iso) {
  if (!iso) return "—";
  const d = parseAppDateTime(iso);
  if (!d) return String(iso);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function formatTimeOnly(iso) {
  if (!iso) return "—";
  const d = parseAppDateTime(iso);
  if (!d) return String(iso);
  return d.toLocaleTimeString(undefined, TIME_FORMAT);
}

const DAY_MS = 24 * 60 * 60 * 1000;

export function getExpiryStatus(expiryDateStr) {
  if (!expiryDateStr) {
    return { key: "none", label: "No expiry", expired: false, soon: false, daysLeft: null };
  }

  const t = new Date(`${expiryDateStr}T23:59:59`).getTime();
  if (Number.isNaN(t)) {
    return { key: "unknown", label: "Unknown", expired: false, soon: false, daysLeft: null };
  }

  const now = Date.now();
  const daysLeft = Math.ceil((t - now) / DAY_MS);

  if (daysLeft < 0) {
    return { key: "expired", label: "Expired", expired: true, soon: false, daysLeft };
  }
  if (daysLeft <= 30) {
    return { key: "1m", label: "1 month", expired: false, soon: true, daysLeft };
  }
  if (daysLeft <= 60) {
    return { key: "2m", label: "2 months", expired: false, soon: true, daysLeft };
  }
  if (daysLeft <= 90) {
    return { key: "3m", label: "3 months", expired: false, soon: true, daysLeft };
  }

  return { key: "ok", label: "Fresh", expired: false, soon: false, daysLeft };
}

export function isExpirySoonOrPast(expiryDateStr) {
  const status = getExpiryStatus(expiryDateStr);
  return { past: status.expired, soon: status.soon };
}
